import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { API, setAdminToken, getAdminToken, formatApiError } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/brand";
import { useNoIndex } from "@/lib/useNoIndex";

export default function AdminLogin() {
    useNoIndex();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (getAdminToken()) {
        return <Navigate to="/admin/questionnaire" replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Please enter your email and password.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, {
                email: email.trim(),
                password,
            });
            setAdminToken(data.access_token);
            toast.success("Welcome back.");
            navigate("/admin/questionnaire", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3.5 text-sm placeholder:text-[#666] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all duration-300 font-body";

    return (
        <main
            data-testid="admin-login-page"
            className="relative min-h-screen bg-black text-white font-body flex items-center justify-center px-6 py-16 luxe-radial"
        >
            <div className="grain absolute inset-0 pointer-events-none" />

            <div className="relative w-full max-w-md">
                <div className="flex items-center justify-center mb-10">
                    <img
                        src={LOGO_URL}
                        alt="RecoveryDesk"
                        className="h-16 w-16 object-cover rounded-sm ring-1 ring-[#D4AF37]/40"
                    />
                </div>

                <div className="relative border border-white/[0.06] bg-[#050505] p-8 md:p-10">
                    <div className="absolute top-0 left-0 w-12 h-px bg-[#D4AF37]" />
                    <div className="absolute top-0 left-0 h-12 w-px bg-[#D4AF37]" />
                    <div className="absolute bottom-0 right-0 w-12 h-px bg-[#D4AF37]" />
                    <div className="absolute bottom-0 right-0 h-12 w-px bg-[#D4AF37]" />

                    <div className="flex items-center gap-3 mb-3">
                        <Lock className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                        <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
                            Admin Access
                        </span>
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-white mb-8 leading-tight">
                        Sign in to the desk.
                    </h1>

                    <form onSubmit={onSubmit} data-testid="admin-login-form" noValidate>
                        <label
                            className="text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] mb-2 block font-medium"
                            htmlFor="admin-email"
                        >
                            Email
                        </label>
                        <input
                            id="admin-email"
                            data-testid="admin-email-input"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputClass}
                            placeholder="you@recoverydesk.com"
                            autoComplete="username"
                            required
                        />

                        <label
                            className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] mb-2 block font-medium"
                            htmlFor="admin-password"
                        >
                            Password
                        </label>
                        <input
                            id="admin-password"
                            data-testid="admin-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={inputClass}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            required
                        />

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Signing in…
                                </>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </form>
                </div>

                <p className="mt-8 text-center text-xs text-[#666]">
                    Restricted area · RecoveryDesk
                </p>
            </div>
        </main>
    );
}
